/**
 * Fordonskort — fliken "Allmän information": besiktning/skatt från Car.info, referenser från ekonomi.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation, useLanguage } from '@/store/languageStore';
import { extractTaxMonthValues, nextTaxDueDate, parseTaxPaymentMonths } from '@/utils/taxDueCalc';
import type { CarInfoData } from '@/types/vehicle';
import { getCostCenters, getEconomy } from '@/services/vehicleEconomy/vehicleEconomyService';
import type { CostCenterOption, VehicleEconomy } from '@/types/vehicleEconomy';
import { BusinessResponsibilityField } from '@/components/vehicle/BusinessResponsibilityField';
import { SharedCompanyCarToggle } from '@/components/vehicle/SharedCompanyCarToggle';
import { ownershipLabelKey, resolveOwnershipType } from '@/utils/vehicleOwnership';
import {
  type CarInfoExtendedAttr,
  EXT_IN_TRAFFIC,
  EXT_NEXT_INSPECTION,
  displayAttrValues,
  findSpecAttrById,
  flattenCarInfoSpecAttrs,
  formatCarInfoSekAmount,
  formatCarInfoYyyymmdd,
  getExtendedById,
  latestInspectionFromHistory,
  SPEC_ID_TAXATION_MONTHS,
  SPEC_ID_VEHICLE_MALUS,
  SPEC_ID_VEHICLE_TAX,
} from '@/utils/carInfoGeneral';

interface NamedOption {
  id: number;
  name: string;
}

interface VehicleGeneralInfoTabProps {
  vehicleId: number;
  organizationId: string;
  carInfo: CarInfoData | null;
  alternativeFuels: NamedOption[];
  isLoadingAlternativeFuels: boolean;
  alternativeFuelId: string;
  onAlternativeFuelChange: (value: string) => void;
  usageStatuses: NamedOption[];
  isLoadingUsageStatuses: boolean;
  usageStatusId: string;
  onUsageStatusChange: (value: string) => void;
  businessResponsibilityId: string;
  onBusinessResponsibilityChange: (value: string) => void;
  businessResponsibilityLabel?: string | null;
  businessResponsibilityIkpartLabel?: string | null;
  tempFordonsansvarigRsid?: string | null;
  tempFordonsansvarigName?: string | null;
  tempFordonsansvarigEmail?: string | null;
  tempFordonsansvarigPhone?: string | null;
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-textDefault mb-3">
      <span className="inline-block w-1 self-stretch min-h-[1rem] rounded-sm bg-primary" aria-hidden />
      {children}
    </h3>
  );
}

function FieldGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>;
}

function ReadField({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'danger';
}) {
  const valueClass =
    tone === 'danger'
      ? 'text-sm font-medium text-red-700 dark:text-red-300 break-words'
      : 'text-sm font-medium text-textDefault break-words';
  return (
    <div className="rounded-lg border border-border bg-bgBase px-3 py-2">
      <div className="text-xs text-textSecondary mb-0.5">{label}</div>
      <div className={valueClass}>{value || '—'}</div>
    </div>
  );
}

function formatCarInfoTernary(
  value: string | null | undefined,
  t: (key: string) => string
): string {
  switch (value) {
    case 'yes':
      return t('vehicleCard.general.ternaryYes');
    case 'no':
      return t('vehicleCard.general.ternaryNo');
    case 'n/a':
      return t('vehicleCard.general.ternaryNA');
    case null:
    case undefined:
    case '':
      return '';
    default: {
      const _unknown: string = value;
      return _unknown;
    }
  }
}

function resolveCostCenterLabel(
  id: string | null | undefined,
  centers: CostCenterOption[]
): string | null {
  if (!id) return null;
  const c = centers.find((x) => x.id === id);
  if (!c) return id;
  return `${c.code} – ${c.description}`;
}

export const VehicleGeneralInfoTab: React.FC<VehicleGeneralInfoTabProps> = ({
  vehicleId,
  organizationId,
  carInfo,
  alternativeFuels,
  isLoadingAlternativeFuels,
  alternativeFuelId,
  onAlternativeFuelChange,
  usageStatuses,
  isLoadingUsageStatuses,
  usageStatusId,
  onUsageStatusChange,
  businessResponsibilityId,
  onBusinessResponsibilityChange,
  businessResponsibilityLabel,
  businessResponsibilityIkpartLabel,
  tempFordonsansvarigRsid,
  tempFordonsansvarigName,
  tempFordonsansvarigEmail,
  tempFordonsansvarigPhone,
}) => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [economy, setEconomy] = useState<VehicleEconomy | null>(null);
  const [costCenters, setCostCenters] = useState<CostCenterOption[]>([]);
  const [loadingEco, setLoadingEco] = useState(true);

  const loadEconomy = useCallback(async () => {
    setLoadingEco(true);
    try {
      const [e, cc] = await Promise.all([getEconomy(vehicleId), getCostCenters(organizationId)]);
      setEconomy(e);
      setCostCenters(cc);
    } catch {
      setEconomy(null);
      setCostCenters([]);
    } finally {
      setLoadingEco(false);
    }
  }, [vehicleId, organizationId]);

  useEffect(() => {
    void loadEconomy();
  }, [loadEconomy]);

  const ext = (carInfo?.extendedAttributes ?? []) as CarInfoExtendedAttr[];
  const nextInspRaw = getExtendedById(ext, EXT_NEXT_INSPECTION)?.values?.[0];
  const nextInsp = formatCarInfoYyyymmdd(nextInspRaw) ?? (nextInspRaw != null ? String(nextInspRaw) : null);

  const inTraffic = getExtendedById(ext, EXT_IN_TRAFFIC);
  const inTrafficVal = displayAttrValues(inTraffic?.values, inTraffic?.unit);
  const registerDate = carInfo?.registerDate
    ? (() => {
        try {
          return new Date(carInfo.registerDate!).toLocaleDateString('sv-SE');
        } catch {
          return carInfo.registerDate ?? null;
        }
      })()
    : null;
  const trafficAffirm = /^(ja|yes|j)$/i.test(String(inTrafficVal || '').trim());
  const påställdText =
    trafficAffirm && registerDate
      ? `${inTrafficVal}, sedan ${registerDate}`
      : inTrafficVal || registerDate || null;

  const lastInsp = latestInspectionFromHistory(carInfo?.history as any[]);
  const lastInspDate = lastInsp?.date
    ? (() => {
        try {
          return new Date(String(lastInsp.date)).toLocaleDateString('sv-SE');
        } catch {
          return String(lastInsp.date);
        }
      })()
    : null;
  const lastInspKm =
    lastInsp?.km !== undefined && lastInsp?.km !== null ? `${Number(lastInsp.km).toLocaleString('sv-SE')} km` : null;
  const lastInspPlace = [lastInsp?.city, lastInsp?.company_name].filter(Boolean).join(' · ') || null;
  const lastInspResult = lastInsp?.inspection_type || lastInsp?.action || null;

  const specsFlat = flattenCarInfoSpecAttrs(carInfo?.specifications);
  const vehicleTaxSpec = findSpecAttrById(specsFlat, SPEC_ID_VEHICLE_TAX);
  const malusSpec = findSpecAttrById(specsFlat, SPEC_ID_VEHICLE_MALUS);
  const taxationMonthsSpec = findSpecAttrById(specsFlat, SPEC_ID_TAXATION_MONTHS);

  const carInfoTaxStr = vehicleTaxSpec ? formatCarInfoSekAmount(vehicleTaxSpec.values) : '';
  // Fallback för fordon där Car.info saknar skatt (t.ex. lastbilar): visa manuellt
  // angiven årsskatt = vehicle_economy.cost_month_tax × 12. Varaktigt i MariaDB (skrivs
  // inte över av Car.info-synk) och konsekvent med TCO-rapportens fallback.
  const ecoMonthlyTax = economy?.cost_month_tax;
  const fallbackAnnualTaxStr =
    !carInfoTaxStr && ecoMonthlyTax != null && Number(ecoMonthlyTax) > 0
      ? formatCarInfoSekAmount([Number(ecoMonthlyTax) * 12])
      : '';
  const taxAmountStr = carInfoTaxStr || fallbackAnnualTaxStr;
  const malusStr = malusSpec ? formatCarInfoSekAmount(malusSpec.values) : '';
  const taxMonthValues = extractTaxMonthValues(taxationMonthsSpec);
  const taxationMonthsStr = taxMonthValues.length
    ? displayAttrValues(taxMonthValues, taxationMonthsSpec?.unit)
    : '';
  const nextTaxDue = nextTaxDueDate(parseTaxPaymentMonths(taxMonthValues), new Date());
  const nextTaxDueStr = nextTaxDue
    ? nextTaxDue.toLocaleDateString(language === 'sv' ? 'sv-SE' : language === 'lt' ? 'lt-LT' : 'en-GB')
    : '';

  const ccLabel =
    resolveCostCenterLabel(economy?.cost_center_id_leasing, costCenters) ||
    resolveCostCenterLabel(economy?.cost_center_id_insurance, costCenters) ||
    resolveCostCenterLabel(economy?.cost_center_id_tax, costCenters) ||
    resolveCostCenterLabel(economy?.cost_center_id_tax_malus, costCenters);

  const ownership = resolveOwnershipType(economy?.ownership_type, economy?.rent_end);
  const refRows: { label: string; value: string | null }[] = [
    { label: t('vehicleCard.general.ownership'), value: ownership ? t(ownershipLabelKey(ownership)) : null },
    { label: t('vehicleCard.general.leasingAgreementNo'), value: economy?.agreement_no ?? null },
    { label: t('vehicleCard.general.contractNo'), value: economy?.contract_no ?? null },
    { label: t('vehicleCard.general.costCenter'), value: ccLabel },
  ];
  const hasAnyRef = refRows.some((r) => r.value && String(r.value).trim() !== '');

  const hasTempFordonsansvarig = [
    tempFordonsansvarigRsid,
    tempFordonsansvarigName,
    tempFordonsansvarigEmail,
    tempFordonsansvarigPhone,
  ].some((v) => v != null && String(v).trim() !== '');

  return (
    <div className="space-y-6">
      <SharedCompanyCarToggle vehicleId={vehicleId} />
      <section>
        <SectionTitle>{t('vehicleCard.general.sectionVehicleAttributes')}</SectionTitle>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <BusinessResponsibilityField
              vehicleId={vehicleId}
              organizationId={organizationId}
              value={businessResponsibilityId}
              onChange={onBusinessResponsibilityChange}
              currentLabel={businessResponsibilityLabel}
              savedIkpartLabel={businessResponsibilityIkpartLabel}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-textSecondary block mb-1">
                  {t('vehicleCard.general.alternativeFuel')}
                </label>
                <select
                  value={alternativeFuelId}
                  onChange={(e) => onAlternativeFuelChange(e.target.value)}
                  disabled={isLoadingAlternativeFuels}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgLight text-textDefault focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">{t('vehicleCard.general.noAlternativeFuel')}</option>
                  {alternativeFuels.map((fuel) => (
                    <option key={fuel.id} value={String(fuel.id)}>
                      {fuel.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-textSecondary block mb-1">
                  {t('vehicleCard.general.usageStatus')}
                </label>
                <select
                  value={usageStatusId}
                  onChange={(e) => onUsageStatusChange(e.target.value)}
                  disabled={isLoadingUsageStatuses}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgLight text-textDefault focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">{t('vehicleCard.general.noUsageStatus')}</option>
                  {usageStatuses.map((status) => (
                    <option key={status.id} value={String(status.id)}>
                      {status.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          {hasTempFordonsansvarig && (
            <div className="space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-textSecondary">
                {t('vehicleCard.general.tempFordonsansvarigSection')}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <ReadField
                  label={t('vehicleCard.general.tempFordonsansvarigRsid')}
                  value={tempFordonsansvarigRsid ?? ''}
                />
                <ReadField
                  label={t('vehicleCard.general.tempFordonsansvarigName')}
                  value={tempFordonsansvarigName ?? ''}
                />
                <ReadField
                  label={t('vehicleCard.general.tempFordonsansvarigEmail')}
                  value={tempFordonsansvarigEmail ?? ''}
                />
                <ReadField
                  label={t('vehicleCard.general.tempFordonsansvarigPhone')}
                  value={tempFordonsansvarigPhone ?? ''}
                />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Besiktning */}
      <section>
        <SectionTitle>{t('vehicleCard.general.sectionInspection')}</SectionTitle>
        {!carInfo && (
          <p className="text-sm text-textSecondary">{t('vehicleCard.general.noCarInfo')}</p>
        )}
        {carInfo && (
          <div className="space-y-4">
            <FieldGrid>
              <ReadField label={t('vehicleCard.general.lastInspection')} value={lastInspDate ?? ''} />
              <ReadField label={t('vehicleCard.general.inspectionResult')} value={lastInspResult ?? ''} />
              <ReadField label={t('vehicleCard.general.odometerAtInspection')} value={lastInspKm ?? ''} />
              <ReadField label={t('vehicleCard.general.nextInspectionDue')} value={nextInsp ?? ''} />
              <ReadField label={t('vehicleCard.general.inspectionStation')} value={lastInspPlace ?? ''} />
            </FieldGrid>
          </div>
        )}
      </section>

      {/* Skatt */}
      <section>
        <SectionTitle>{t('vehicleCard.general.sectionTax')}</SectionTitle>
        {!carInfo && !taxAmountStr && (
          <p className="text-sm text-textSecondary">{t('vehicleCard.general.noCarInfo')}</p>
        )}
        {(carInfo || taxAmountStr) && (
          <div className="space-y-4">
            <FieldGrid>
              <ReadField
                label={t('vehicleCard.general.annualVehicleTax')}
                value={taxAmountStr || '—'}
              />
              <ReadField label={t('vehicleCard.general.malus')} value={malusStr || '—'} />
              <ReadField
                label={t('vehicleCard.general.taxationMonths')}
                value={taxationMonthsStr || '—'}
              />
              <ReadField
                label={t('vehicleCard.general.nextTaxDue')}
                value={nextTaxDueStr || '—'}
              />
            </FieldGrid>
            {taxationMonthsStr && (
              <p className="text-xs text-textSecondary">{t('vehicleCard.general.taxDueHint')}</p>
            )}
            <FieldGrid>
              {(inTrafficVal || registerDate) && (
                <ReadField label={t('vehicleCard.general.registeredForUse')} value={påställdText ?? ''} />
              )}
              {carInfo?.emergencyVehicle && (
                <ReadField
                  label={t('vehicleCard.general.emergencyVehicle')}
                  value={formatCarInfoTernary(carInfo.emergencyVehicle, t)}
                />
              )}
              {carInfo?.hasDrivingBan && (
                <ReadField
                  label={t('vehicleCard.general.hasDrivingBan')}
                  value={formatCarInfoTernary(carInfo.hasDrivingBan, t)}
                  tone={carInfo.hasDrivingBan === 'yes' ? 'danger' : 'default'}
                />
              )}
            </FieldGrid>
            {taxationMonthsStr && (
              <p className="text-xs text-textSecondary">{t('vehicleCard.general.taxDueHint')}</p>
            )}
            {!taxAmountStr &&
              !malusStr &&
              !taxationMonthsStr &&
              !inTrafficVal &&
              !registerDate && (
              <p className="text-sm text-textSecondary">{t('vehicleCard.general.noTaxAttrs')}</p>
            )}
          </div>
        )}
      </section>

      {/* Referenser */}
      <section>
        <SectionTitle>{t('vehicleCard.general.sectionReferences')}</SectionTitle>
        {loadingEco && (
          <p className="text-sm text-textSecondary">{t('common.loading')}</p>
        )}
        {!loadingEco && !hasAnyRef && (
          <p className="text-sm text-textSecondary">{t('vehicleCard.general.noEconomyRefs')}</p>
        )}
        {!loadingEco && hasAnyRef && (
          <FieldGrid>
            {refRows
              .filter((r) => r.value && String(r.value).trim() !== '')
              .map((r) => (
                <ReadField key={r.label} label={r.label} value={r.value!} />
              ))}
          </FieldGrid>
        )}
      </section>
    </div>
  );
};

export default VehicleGeneralInfoTab;
